import { ApolloError, gql } from "@apollo/client";
import { createContext, ReactNode, useCallback, useMemo, useState } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import SkeletonLoader from "components/loading/SkeletonLoader";
import { BaseSelectItem } from "ds/components/Select/types";
import useTypedContext from "hooks/useTypedContext";
import { Space } from "types/generated";
import { hasAdminAccess } from "utils/access";
import { generateSpaceHierarchyMap, getFullSpaceNamePath, isRootSpace } from "utils/space";
import { AccountContext } from "views/AccountWrapper";

import { PollingIntervalGroups } from "../../apollo/constants";

export type SpacesContextType = {
  spaces: Space[];
  spaceHierarchy: Record<string, string>;
  hasManageableSpaces: boolean;
  hasEntityCreateAccess: boolean;
  manageableSpaces: Space[];
  manageableSpacesSelectOptions: BaseSelectItem[];
  error?: ApolloError;
  startSpacesPolling: () => void;
  stopSpacesPolling: () => void;
  hasRootSpaceAccess: boolean;
  canNonRootAdminCreateSpace: boolean;
};

/** @public (Knip ignored - used in the file and exported for testing) */
export const GET_SPACES = gql`
  query GetSpaces {
    spaces {
      id
      name
      description
      parentSpace
      inheritEntities
      accessLevel
      labels
    }
    allowNonRootAdminSpaceCreation
  }
`;

export const SpacesContext = createContext<SpacesContextType | undefined>(undefined);
SpacesContext.displayName = "SpacesContext";

type SpacesProviderProps = {
  children: ReactNode;
};

const SpacesProvider = ({ children }: SpacesProviderProps) => {
  const { viewer } = useTypedContext(AccountContext);
  const [shouldPoll, setShouldPoll] = useState(false);

  const { data, error } = usePolledQuery<{
    spaces: Space[];
    allowNonRootAdminSpaceCreation: boolean;
  }>(GET_SPACES, {
    shouldPoll: () => shouldPoll,
    pollingGroup: PollingIntervalGroups.Lists,
  });

  const startSpacesPolling = useCallback(() => {
    setShouldPoll(true);
  }, []);

  const stopSpacesPolling = useCallback(() => {
    setShouldPoll(false);
  }, []);

  const spaces = data?.spaces;

  const manageableSpaces = useMemo(() => {
    if (spaces) {
      return spaces.filter((space) => hasAdminAccess(viewer, space.id));
    }

    return [];
  }, [spaces, viewer]);

  const hasRootSpaceAdminLevel = useMemo(() => {
    return !!manageableSpaces.find((space) => isRootSpace(space.id));
  }, [manageableSpaces]);

  const spaceHierarchy = useMemo(() => generateSpaceHierarchyMap(spaces), [spaces]);

  const hasManageableSpaces = manageableSpaces.length > 0;

  const manageableSpacesSelectOptions = useMemo(
    () =>
      manageableSpaces.map((item) => ({
        value: item.id,
        // TODO: Remove this once backend returns full space path
        label: getFullSpaceNamePath(item.id, spaceHierarchy, spaces ?? []),
      })),
    [spaces, manageableSpaces, spaceHierarchy]
  );

  const isLoading = !data;

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <SpacesContext.Provider
      value={{
        spaces: data.spaces,
        manageableSpaces,
        manageableSpacesSelectOptions,
        hasManageableSpaces,
        spaceHierarchy,
        // FYI: use for creating a new entity only, not for editing - in editing mode, first of all, should be verified an item space access level
        hasEntityCreateAccess: viewer.admin || hasRootSpaceAdminLevel || hasManageableSpaces,
        hasRootSpaceAccess: viewer.admin || hasRootSpaceAdminLevel,
        canNonRootAdminCreateSpace: !!data?.allowNonRootAdminSpaceCreation,
        startSpacesPolling,
        stopSpacesPolling,
        error,
      }}
    >
      {children}
    </SpacesContext.Provider>
  );
};

export default SpacesProvider;
